import { Component, signal } from "@angular/core";
import { Router } from "@angular/router";
import { ActivatedRoute } from "@angular/router";
import { FormControl, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { AbstractAdvisorComponent } from "../abstract/advisor.component";
import { AuthService } from "../../service/auth.service";
import { AnalyticsService } from "../../service/analytics.service";
import { GeneralService } from "../../service/general.service";
import { ValidationService } from "../../service/validation.service";
import { User } from "../../entity/User";
import { Company } from "../../entity/Company";
import { State } from "../../entity/State";
import { Pair } from "../../obj/Pair";
import { HeaderComponent } from "../incs/header.component";
import { NgxMaskDirective } from "ngx-mask";
import { OneClickDirective } from "../helpers/one-click.component";
import { firstValueFrom } from "rxjs/internal/firstValueFrom";
import { ContactManagerDialog } from "../incs/contact-manager-dialog.component";

@Component({
    selector: "kilowant-client-details",
    templateUrl: "../../../html/advisor/main-client-details.html",
    imports: [HeaderComponent, FormsModule, NgxMaskDirective, ReactiveFormsModule, OneClickDirective]
})
export class ClientDetailsComponent extends AbstractAdvisorComponent {
    currentPhoneNumber: FormControl = new FormControl("");
    clientInfo: User = new User();
    confirmButtonActive: boolean = true;
    fileToUpload: File | null = null;
    taxExemptCertificateDocument: Pair | null = null;
    federalTaxID: string;
    deleteTaxExemptCertificateDocument: boolean;
    taxExempt: boolean;
    cityStateShow = signal<boolean>(false);
    clientType: string = "";
    editContact: User;
    externalContact: Pair | null;
    clientEditDisabled = signal<boolean>(false);
    contactManagers: Pair[];
    kilowantAccess: number = 0;

    constructor(
        router: Router,
        route: ActivatedRoute,
        authService: AuthService,
        analyticsService: AnalyticsService,
        generalService: GeneralService,
        public validationService: ValidationService,
        dialog: MatDialog
    ) {
        super(router, route, dialog, authService, analyticsService, generalService);
    }

    ngOnInit(): void {
        firstValueFrom(this.generalService.getContactManagers()).then((x: any) => { this.contactManagers = x; });
    }

    confirm(): void {
        this.clearErrors();
        if (this.clientInfo.alias == null || this.clientInfo.alias.length == 0) {
            this.addError("ClientName");
        }
        if (this.externalContact == null) {
            if (this.clientInfo.firstName == null || this.clientInfo.firstName.length == 0) {
                this.addError("FirstName");
            }
            if (this.clientInfo.lastName == null || this.clientInfo.lastName.length == 0) {
                this.addError("LastName");
            }
            if (this.clientInfo.email == null || this.clientInfo.email.length == 0) {
                this.addError("Email");
            }
        }
        if (this.clientInfo.addressLine1 == null || this.clientInfo.addressLine1 == "") {
            this.addError("addressLine1");
        }
        if (this.clientInfo.zip == null || this.clientInfo.zip == "") {
            this.addError("zip");
        }
        if (this.currentPhoneNumber.value == null || this.currentPhoneNumber.value.length == 0) {
            this.addError("Phone");
        }

        if ((this.clientType == "Commercial") && this.taxExempt == true && (this.federalTaxID == null || this.federalTaxID.length == 0)) {
            this.addError("FederalTaxID");
        }
        if ((this.clientType == "Commercial") && this.taxExempt == true && this.fileToUpload == null && this.taxExemptCertificateDocument == null) {
            this.addError("fileToUpload");
        }

        if (this.errorY().showError()) {
            this.setErrorText("Please enter the data requested above.");
            this.confirmButtonActive = true;
            return;
        }

        if (this.clientInfo.city == null || this.clientInfo.city.length == 0 || this.clientInfo.state == null) {
            this.addError("zip");
            this.setErrorText("Zip code is not correct.");
            return;
        }

        if (this.externalContact == null &&  !this.currentPhoneNumber.valid) {
            this.addError("Phone");
            this.setErrorText("Phone number is not correct.");
            this.confirmButtonActive = true;
            return;
        }
        this.clientInfo.phone = this.currentPhoneNumber.value;
        this.clientInfo.isActive = true;

        if ((this.clientType == "Commercial")) {
            this.clientInfo.company!.name = this.clientInfo.alias;
            this.clientInfo.company!.state = this.clientInfo.state!;
            this.clientInfo.company!.city = this.clientInfo.city!;
            this.clientInfo.company!.zip = this.clientInfo.zip!;
            this.clientInfo.company!.addressLine1 = this.clientInfo.addressLine1!;
            this.clientInfo.company!.addressLine2 = this.clientInfo.addressLine2!;
            this.clientInfo.company!.phone = this.clientInfo.phone!;
            this.clientInfo.company!.taxExempt = this.taxExempt;
            this.clientInfo.company!.federalTaxID = this.federalTaxID;
        }


        this.generalService.registerUser(this.clientInfo, (this.externalContact == null) ? 0 : this.externalContact.key, null, null, this.fileToUpload, true)
            .subscribe({
                next: (res: any) => {
                    if (res) {
                        this.clientInfo.id = res;
                        this.clientInfo.nameAsClient = this.clientInfo.alias;
                        this.generalService.switchStoredClient(this.clientInfo, null);
                        this.router.navigate(["/account/add"]);
                    }
                },
                error: (err: any) => {
                    if (parseInt(err.status) == 400) {
                        this.addError("Email");
                        this.setErrorText(err.error);
                        this.confirmButtonActive = true;
                    }
                }
            });
    }

    downloadFile(ifExisting: boolean, id: number | null) {
        if (!ifExisting) {
            this.generalService.sendFileToClient(this.fileToUpload);
        } else if (id != null) {
            this.generalService.openDocument(id);
        }
    }

    deleteFile() {
        this.fileToUpload = null;
    }

    switchClientType(): void {
        if (this.clientType == "Commercial" && this.clientInfo!.company == null) {
            this.clientInfo!.company = new Company();
        }
    }

    handleFileInput(event: Event) {
        const target = event.target as HTMLInputElement;
        this.fileToUpload = (target.files as FileList).item(0) as File;
        this.resetError("fileToUpload");
    }

    deleteExistingFile(key: number) {
        this.deleteTaxExemptCertificateDocument = true;
        this.taxExemptCertificateDocument = null!;
    }

    getStateName(state: State | null): string {
        if (state == null) {
            return "";
        }
        return state.stateCode;
    }

    zipChanged(): void {
        this.resetError('zip');
        this.clientInfo.city = "";
        this.clientInfo.state = null;
        if (this.clientInfo.zip != null && this.clientInfo.zip?.length > 2) {
            let request = this.generalService.getStateCityByZip(this.clientInfo.zip);
            if (request == null) {
                return;
            }
            firstValueFrom(request).then(data => {
                if (data != null) {
                    this.clientInfo.state = data.key;
                    this.clientInfo.city = data.value;
                    this.cityStateShow.set(true);
                    this.refreshView();
                }
            });
        }
    }

    goBack(): void {
        this.router.navigate(["/user/home"]);
    }

    changeClientContact(): void {
        if (this.kilowantAccess == 1) {
            this.externalContact = null;
            this.editContact = this.clientInfo!;
            this.currentPhoneNumber.setValue(this.clientInfo!.phone);
            this.clientEditDisabled.set(false);
        } else {
            this.resetError("FirstName");
            this.resetError("LastName");
            this.resetError("Email");
            this.resetError("Phone");
            this.externalContact = this.contactManagers[0];
            this.changeExternalContact();
        }
        this.refreshView();
    }

    changeExternalContact(): void {
        if (this.externalContact != null && this.externalContact.key > 0) {
            this.setEditContactBasedOnLink();
        } else {
            this.clientEditDisabled.set(false);
        }
        if (this.externalContact == null) {
            this.addUpdateContactManager();
        }
        this.refreshView();
    }

    setEditContactBasedOnLink(): void {
        if (this.externalContact != null && this.externalContact.key > 0) {
            firstValueFrom(this.generalService.getClient(this.externalContact.key))
                .then((res: any) => {
                    this.editContact = res.key;
                    this.currentPhoneNumber.setValue(res.key.phone);
                    this.refreshView();
                });
            this.clientEditDisabled.set(true);
        }
    }

    addUpdateContactManager(): void {
        var dialogRef = this.dialog.open(ContactManagerDialog, {
            disableClose: false,
            panelClass: "card-popup"
        });
        if (this.externalContact != null) {
            dialogRef.componentInstance.loadContact(this.externalContact.key);
        }
        dialogRef.afterClosed().subscribe(result => {
            if (result > 0) {
                if (this.externalContact != null) {
                    this.externalContact = new Pair(result, this.externalContact!.value);
                } else {
                    firstValueFrom(this.generalService.getContactManagers()).then((x: any) => {
                        this.contactManagers = x;
                        for (let i = 0; i < this.contactManagers.length; i++) {
                            if (this.contactManagers[i].key == result) {
                                this.kilowantAccess = 2;
                                this.externalContact = new Pair(result, this.contactManagers[i].value);
                                this.changeExternalContact();
                                return;
                            }
                        }                        
                    });
                }
            }
            if (this.externalContact == null && this.kilowantAccess == 2) {
                this.kilowantAccess = 1;
                this.changeClientContact();
            } else {
                this.changeExternalContact();
                firstValueFrom(this.generalService.getContactManagers()).then((x: any) => { this.contactManagers = x; });
            }
        });
    }


}
